import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";

async function uploadChunked(file, meta, onProgress) {
  const { data: up } = await api.post("/documents/uploads", { ...meta, filename: file.name, size: file.size });
  for (let i = 0; i < up.chunks; i++) {
    const blob = file.slice(i * up.chunk_size, (i + 1) * up.chunk_size);
    let tries = 0;
    for (;;) {
      try {
        await api.put(`/documents/uploads/${up.upload_id}/chunks/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
        break;
      } catch (e) {
        if (++tries >= 3) throw e;
      }
    }
    onProgress(Math.round(((i + 1) / up.chunks) * 90));
  }
  const { data } = await api.post(`/documents/uploads/${up.upload_id}/complete`);
  onProgress(100);
  return data;
}

export const DocUploadDialog = ({ open, onOpenChange, types, onDone, replaces }) => {
  const [commesse, setCommesse] = useState([]);
  const [f, setF] = useState({ commessa_id: "", title: "", doc_type: "capitolato" });
  const [file, setFile] = useState(null);
  const [pct, setPct] = useState(null);
  useEffect(() => {
    if (open) {
      api.get("/commesse").then((r) => setCommesse(r.data));
      setF(replaces ? { commessa_id: replaces.commessa_id, title: replaces.title, doc_type: replaces.doc_type } : { commessa_id: "", title: "", doc_type: "capitolato" });
      setFile(null);
      setPct(null);
    }
  }, [open, replaces]);
  const go = async () => {
    try {
      setPct(0);
      const d = await uploadChunked(file, { ...f, replaces_id: replaces?.id || null }, setPct);
      toast.success(`Caricato: ${d.pages} pagine`);
      onOpenChange(false);
      onDone(d);
    } catch (e) {
      toast.error(errMsg(e));
      setPct(null);
    }
  };
  const busy = pct !== null;
  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent aria-describedby={undefined} className="max-w-lg rounded-2xl" data-testid="doc-upload-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">{replaces ? "Nuova versione" : "Carica documento"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>Commessa *</Label>
            <NativeSelect value={f.commessa_id} onChange={(v) => setF({ ...f, commessa_id: v })} disabled={!!replaces} className="w-full" testid="doc-upload-commessa">
              <option value="">Seleziona…</option>{commesse.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
            </NativeSelect></div>
          <div className="space-y-1"><Label>Titolo *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="doc-upload-title" /></div>
          <div className="space-y-1"><Label>Tipo</Label>
            <NativeSelect value={f.doc_type} onChange={(v) => setF({ ...f, doc_type: v })} className="w-full" testid="doc-upload-type">
              {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </NativeSelect></div>
          <div className="space-y-1"><Label>File PDF *</Label><Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-upload-file" /></div>
          {busy && <div data-testid="doc-upload-progress"><Progress value={pct} /><p className="mt-1 text-xs text-muted-foreground">{pct < 90 ? `Caricamento a blocchi… ${pct}%` : "Estrazione del testo pagina per pagina…"}</p></div>}
        </div>
        <DialogFooter>
          <Button onClick={go} disabled={busy || !file || !f.commessa_id || f.title.length < 3} className="rounded-xl" data-testid="doc-upload-submit"><Upload className="mr-1 h-4 w-4" />Carica</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
